/**
 * Ce que le certificat d'atelier inscrit, et rien d'autre.
 *
 * ⭐ La page /certification n'est pas un rapport de conformité, c'est la marque
 * que l'atelier appose au dos de ce qu'il fabrique. Le modèle est la plaque de
 * l'ébéniste, le poinçon de l'orfèvre, le colophon du livre d'art. Le
 * certificateur est nommé, ses critères sont publiés, le numéro est unique :
 * c'est une garantie de fabricant, pas un label décerné par un tiers.
 *
 * ⛔ CE FICHIER REMPLACE LE REGISTRE DU CONSTAT. Une version précédente de la
 * page refusait le mot « certifié » et s'interdisait tout vocabulaire de
 * métier. Ce cadrage est abandonné : on certifie son propre produit selon ses
 * propres critères publiés, et le lexique technique se montre justement parce
 * qu'il est le métier.
 *
 * ⚠️ Une seule valeur vient de la machine, l'empreinte de build. Tout le reste
 * est une inscription, au sens du registre : elle est décidée, elle est datée,
 * et elle ne se recalcule pas.
 */

import { NOM_AGENCE, SITE_AGENCE } from './marque'

/**
 * ⛔ Le nom de l'atelier se LIT à sa source, il ne se recopie pas ici. Deux
 * écritures d'un même nom, c'est une divergence qui attend son jour : le
 * certificat dirait une chose et les métadonnées de la page une autre, et
 * personne ne comparerait. `tests/marque.test.ts` fait échouer le build sur
 * une copie.
 */

/**
 * ⭐ LE GABARIT DU KIT. Ce fichier se recopie tel quel a chaque mise a jour.
 * Les donnees de la piece vivent dans `piece.ts`, qui ne se recopie jamais.
 * Il est reexporte ici pour que les composants gardent un seul point d'import.
 */
export * from './piece'
import {
  CODE_ATELIER, DEDANS, DERNIER_CONTROLE, NUMERO, OUVRAGE, OUVRAGE_LONG,
  SORTIE_ATELIER, STATUT, SUR_MESURE,
} from './piece'

export const ATELIER = NOM_AGENCE

/* ══════════════════════════ CE QUI SE DÉDUIT ══════════════════════════════ */
/*
 * ⭐ TOUT CE QUI SUIT SE CALCULE, DONC NE SE REMPLIT PAS. Une pièce ne
 * renseigne que six champs. Chaque valeur dérivée ici est une case en moins à
 * remplir, et surtout une case en moins où se tromper : une divergence entre le
 * numéro et ses segments, ou entre l'édition écrite et le millésime du numéro,
 * ne lèverait aucune erreur et se lirait très bien.
 */

/** Le millésime, lu dans le numéro. Il ne s'écrit pas deux fois. */
export const EDITION = NUMERO.split('-')[1] ?? ''

/** Le rang de l'ouvrage, lu dans le numéro. */
export const RANG = NUMERO.split('-')[2] ?? ''

/**
 * Trois lettres tirées des consonnes de l'ouvrage, sans accents.
 * ⚠️ `CODE_ATELIER` de la pièce l'emporte : le calcul tombe parfois mal, et deux
 * pièces ne partagent jamais un code.
 */
function codeDepuis(nom: string): string {
  const consonnes = nom
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .replace(/[AEIOUY]/g, '')
  return (consonnes + 'XXX').slice(0, 3)
}

/**
 * ⭐ Le numéro segmenté : poinçon de maison, millésime, code d'atelier, rang.
 * Un dispositif d'authenticité classique, et c'est ce qui le distingue d'un
 * identifiant technique.
 */
export const SEGMENTS: readonly string[] = [
  'NG',
  EDITION,
  CODE_ATELIER ?? codeDepuis(OUVRAGE),
  RANG.padStart(4, '0'),
]

/** L'édition du standard sous laquelle la pièce est contrôlée. Constante du kit. */
export const EDITION_STANDARD = '2026'

/** Le nom long s'il apporte quelque chose, le nom court sinon. */
export const OUVRAGE_AFFICHE = OUVRAGE_LONG ?? OUVRAGE

/** Le dernier contrôle, qui vaut la date de délivrance tant qu'on n'est pas repassé. */
export const CONTROLE = DERNIER_CONTROLE ?? SORTIE_ATELIER

/** Les récits sont facultatifs : la page les interroge par ces deux drapeaux. */
export const A_SUR_MESURE = SUR_MESURE !== null
export const A_DEDANS = DEDANS !== null

/**
 * L’etat a afficher en regard d’un article du socle.
 *
 * ⛔⛔ ECRIT EN DUR AVANT : la page imprimait « tenu » en face des onze
 * articles, quel que soit le statut, et `ARTICLES` ne portait aucun etat. Le
 * kit etait donc INCAPABLE d’exprimer un article en ecart, alors que le
 * standard l’exige des qu’une certification est suspendue : la mention doit
 * nommer l’article en cause. Une page qui atteste onze fois « tenu » sans
 * jamais pouvoir dire autre chose n’atteste rien.
 *
 * ⭐ L’etat se DEDUIT donc du statut, une seule source. En vigueur, les onze
 * sont tenus, c’est ce que veut dire l’indivisibilite du socle. Suspendue,
 * l’article nomme passe en ecart et les autres restent tenus. Retiree, aucun
 * etat n’est affirme : il n’y a plus rien a attester.
 */
export function etatArticle(n: number): string | null {
  if (STATUT.etat === 'retirée') return null
  if (STATUT.etat === 'suspendue') {
    return STATUT.article.trim().startsWith(String(n).padStart(2, '0')) ||
      STATUT.article.trim().startsWith(String(n))
      ? 'en écart'
      : 'tenu'
  }
  return 'tenu'
}

export const AUTEUR = 'Meymoun Boualaoui'

export const QUALITE_AUTEUR = 'ingénieur'

/**
 * ⭐ Le numéro segmenté du §0.2 : poinçon de maison, millésime, code d'atelier
 * du projet, rang de l'ouvrage. Il s'affiche trois fois, dans trois matières
 * différentes (gravé en tête, en filigrane de fond, en clair au pied). C'est un
 * dispositif d'authenticité classique, et c'est ce qui le distingue d'un
 * identifiant technique.
 */
/**
 * L'entrée de cette pièce au registre public de l'atelier.
 *
 * ⭐ C'est ce qui sépare un certificat d'une mention de crédit : le numéro
 * renvoie à un registre tenu par celui qui l'a délivré. Sans ce renvoi, la page
 * s'atteste toute seule, et personne ne peut la recouper.
 *
 * ⚠️ L'adresse se CALCULE, elle ne se recopie pas. Le jour où le numéro change,
 * un lien écrit en dur pointerait sur la pièce d'à côté sans que rien ne le
 * signale : une erreur de registre est bien pire qu'un registre absent.
 *
 * ⚠️ `/certifications/` au pluriel, et le numéro en MINUSCULES : c'est la forme
 * arrêtée le 2026-09-07 pour le site de l'atelier. Les deux détails comptent
 * autant que le reste, parce que le site répond 200 à tout chemin inconnu en
 * servant sa page d'accueil. Une faute de casse ne donnerait donc pas une
 * erreur, elle donnerait une page commerciale à quelqu'un venu vérifier un
 * certificat.
 */
export const REGISTRE = `${SITE_AGENCE}/certifications/${NUMERO.toLowerCase()}`

/**
 * Les onze articles du socle, dans l'ordre du standard.
 *
 * ⛔ INDIVISIBLES. Un seul article non tenu, et le projet n'est pas certifié.
 * Il n'y a pas de certification partielle, pas de note, pas de mention de
 * rattrapage : c'est ce qui sépare un standard d'un badge.
 *
 * ⚠️ Une ligne dit le TERME et l'ÉTAT, jamais la méthode. Le refus d'expliquer
 * est une position affichée, pas un manque subi.
 */
export const ARTICLES = [
  { n: 1, nom: 'La fondation neuve' },
  { n: 2, nom: 'Le budget de chargement' },
  { n: 3, nom: 'La charpente sémantique' },
  { n: 4, nom: 'Le plan de site et le maillage interne' },
  { n: 5, nom: "L'indexabilité contrôlée" },
  { n: 6, nom: 'Le parcours de conversion' },
  { n: 7, nom: 'Le plan de mesure' },
  { n: 8, nom: "L'accès pour tous" },
  { n: 9, nom: 'Le verrou des données' },
  { n: 10, nom: 'La souveraineté du client' },
  { n: 11, nom: 'La main unique' },
] as const

/**
 * Les repères de la page, dans l'ordre de lecture.
 *
 * ⭐ UNE SEULE SOURCE POUR TROIS USAGES : l'identifiant de la section, son
 * entrée dans la navigation, et le libellé annoncé à la synthèse vocale. Trois
 * listes séparées finiraient par ne plus dire la même chose, et c'est la
 * navigation qui mentirait en premier, en menant à une ancre qui n'existe plus.
 *
 * ⚠️ `tests/reperes.test.ts` fait échouer le build si un identifiant déclaré ici
 * n'apparaît nulle part dans le code des pages : une ancre morte ne lève aucune
 * erreur, elle ne fait simplement rien quand on clique dessus.
 */
const TOUTES_SECTIONS = [
  { id: 'ouvrage', titre: 'L’ouvrage' },
  { id: 'atelier', titre: 'L’atelier' },
  { id: 'sur-mesure', titre: 'Le sur-mesure' },
  { id: 'dedans', titre: 'Ce qu’il y a dedans' },
  { id: 'socle', titre: 'Le socle' },
  { id: 'certification', titre: 'La certification' },
  { id: 'piece', titre: 'La pièce' },
  { id: 'contact', titre: 'Faire faire une pièce' },
] as const

/**
 * ⛔⛔ LE SOMMAIRE SE FILTRE SUR CE QUI EXISTE VRAIMENT. Les deux récits sont
 * facultatifs : une pièce qui n'en porte aucun est un cas normal, pas un cas
 * dégradé. Sans ce filtre, son sommaire annoncerait deux sections absentes et
 * mènerait à des ancres mortes.
 *
 * ⚠️ Une ancre morte NE LÈVE AUCUNE ERREUR : le clic ne fait simplement rien.
 * C'est le pire des défauts sur une page dont le seul propos est d'être
 * vérifiable, et c'est exactement ce que `tests/reperes.test.ts` surveille.
 */
export const SECTIONS: readonly { id: string; titre: string }[] =
  TOUTES_SECTIONS.filter(
    (s) =>
      (s.id !== 'sur-mesure' || A_SUR_MESURE) &&
      (s.id !== 'dedans' || A_DEDANS),
  )


/**
 * La première section, garantie NON VIDE.
 *
 * ⛔ 🟢 Sous `noUncheckedIndexedAccess`, `SECTIONS[0]` vaut `{...} | undefined`,
 * et le kit ne compilait pas chez un hôte qui active ce réglage (mesuré le
 * 2026-09-09 sur le projet 039). ⚠️ Un hôte SANS ce réglage compile très bien :
 * le défaut est donc invisible partout ailleurs, et il attend le prochain projet
 * strict.
 *
 * ⭐ Résolu ICI, une fois, plutôt que par un `?.` à chaque appel : le repli est
 * alors écrit à un seul endroit, et les composants gardent une valeur `string`.
 * `ouvrage` en dur est sûr : c'est la seule section que le filtre ne retire
 * jamais.
 */
export const PREMIERE_SECTION: string = SECTIONS[0]?.id ?? 'ouvrage'

